import { ref, watch } from "vue";
import {
  getSocialInbox,
  getVapidPublicKey,
  subscribePush,
  unsubscribePush,
} from "@/api/social";
import { tokenStorage } from "@/api/client";
import { useAuth } from "@/composables/useAuth";
import type { SocialNotification } from "@/types";

export const NOTIFICATIONS_ENABLED_KEY = "cocouni.notifications.enabled";
const SEEN_ID_KEY = "cocouni.notifications.seen_id";

const POLL_MS = 8000;

let started = false;
let pollId: ReturnType<typeof setInterval> | null = null;
let registration: ServiceWorkerRegistration | null = null;

export const notificationPermission = ref<NotificationPermission>(
  typeof Notification === "undefined" ? "denied" : Notification.permission
);

export function notificationsEnabled(): boolean {
  return localStorage.getItem(NOTIFICATIONS_ENABLED_KEY) !== "0";
}

export async function setNotificationsEnabled(enabled: boolean): Promise<boolean> {
  localStorage.setItem(NOTIFICATIONS_ENABLED_KEY, enabled ? "1" : "0");
  if (enabled) return enablePush();
  await disablePush();
  return true;
}

export function needsNotificationPermission(): boolean {
  return (
    notificationsEnabled() &&
    typeof Notification !== "undefined" &&
    Notification.permission !== "granted"
  );
}

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  const output = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) output[i] = raw.charCodeAt(i);
  return output;
}

function lastSeenId(): number {
  return Number(localStorage.getItem(SEEN_ID_KEY) || "0") || 0;
}

function setLastSeenId(id: number): void {
  localStorage.setItem(SEEN_ID_KEY, String(id));
}

function canNotify(): boolean {
  return (
    notificationsEnabled() &&
    typeof window !== "undefined" &&
    "Notification" in window &&
    Notification.permission === "granted"
  );
}

function syncPermission(): void {
  if (typeof Notification !== "undefined") {
    notificationPermission.value = Notification.permission;
  }
}

async function showBrowserNotification(item: SocialNotification): Promise<void> {
  if (!canNotify()) return;
  const options: NotificationOptions = {
    body: item.body,
    icon: "/web-app-manifest-192x192.png",
    badge: "/web-app-manifest-192x192.png",
    tag: `social-${item.id}`,
    renotify: true,
    dir: "rtl",
    lang: "fa",
    data: { url: "/#/dashboard" },
  };
  try {
    const worker = await registerWorker();
    if (worker) {
      await worker.showNotification(item.title, options);
      return;
    }
    const n = new Notification(item.title, options);
    n.onclick = () => {
      window.focus();
      n.close();
    };
  } catch {
    try {
      new Notification(item.title, options);
    } catch {
      /* ignore */
    }
  }
}

async function registerWorker(): Promise<ServiceWorkerRegistration | null> {
  if (!("serviceWorker" in navigator)) return null;
  if (registration) return registration;
  try {
    registration = await navigator.serviceWorker.register("/sw.js", {
      updateViaCache: "none",
    });
    await navigator.serviceWorker.ready;
    return registration;
  } catch {
    return null;
  }
}

async function hasPushSubscription(): Promise<boolean> {
  const worker = await registerWorker();
  if (!worker || !("PushManager" in window)) return false;
  const sub = await worker.pushManager.getSubscription();
  return Boolean(sub);
}

async function subscribeToPush(): Promise<void> {
  if (!tokenStorage.getAccess()) return;
  const worker = await registerWorker();
  const vapid = await getVapidPublicKey().catch(() => null);
  if (!worker || !vapid || !("PushManager" in window)) return;

  let sub = await worker.pushManager.getSubscription();
  if (!sub) {
    sub = await worker.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(vapid),
    });
  }
  const json = sub.toJSON();
  if (!json.endpoint || !json.keys?.p256dh || !json.keys?.auth) return;
  await subscribePush({
    endpoint: json.endpoint,
    keys: { p256dh: json.keys.p256dh, auth: json.keys.auth },
  });
}

export async function enablePush(): Promise<boolean> {
  if (!notificationsEnabled()) return false;
  if (!("Notification" in window)) return false;
  if (Notification.permission === "default") {
    try {
      await Notification.requestPermission();
    } catch {
      syncPermission();
      return false;
    }
  }
  syncPermission();
  if (Notification.permission !== "granted") return false;
  try {
    await subscribeToPush();
  } catch {
    /* in-app + SW notifications still work without a push subscription */
  }
  return true;
}

async function syncPushIfGranted(): Promise<void> {
  syncPermission();
  if (!notificationsEnabled()) return;
  if (typeof Notification === "undefined" || Notification.permission !== "granted") {
    return;
  }
  try {
    await subscribeToPush();
  } catch {
    /* ignore */
  }
}

async function disablePush(): Promise<void> {
  const worker =
    registration ||
    (typeof navigator !== "undefined"
      ? await navigator.serviceWorker?.getRegistration()
      : null);
  const sub = await worker?.pushManager.getSubscription();
  if (sub) {
    try {
      await unsubscribePush(sub.endpoint);
    } catch {
      /* ignore */
    }
    await sub.unsubscribe().catch(() => undefined);
  }
}

async function pollInbox(): Promise<void> {
  if (!tokenStorage.getAccess()) return;
  try {
    const inbox = await getSocialInbox();
    const seen = lastSeenId();
    const fresh = inbox.notifications.filter((item) => item.id > seen);
    if (fresh.length) {
      const newest = Math.max(...inbox.notifications.map((item) => item.id));
      if (seen > 0 && !(await hasPushSubscription())) {
        const batch = fresh.slice().reverse().slice(0, 3);
        for (const item of batch) {
          await showBrowserNotification(item);
        }
      }
      setLastSeenId(newest);
    } else if (!seen && inbox.notifications.length) {
      setLastSeenId(Math.max(...inbox.notifications.map((item) => item.id)));
    }
  } catch {
    /* ignore poll errors */
  }
}

export function startPushNotifications(): void {
  if (started) return;
  started = true;
  syncPermission();
  const { isAuthenticated } = useAuth();

  const startPolling = () => {
    if (pollId) return;
    void syncPushIfGranted();
    void pollInbox();
    pollId = setInterval(() => {
      void pollInbox();
    }, POLL_MS);
  };

  const stopPolling = () => {
    if (pollId) {
      clearInterval(pollId);
      pollId = null;
    }
  };

  watch(
    isAuthenticated,
    (authed) => {
      if (authed) startPolling();
      else stopPolling();
    },
    { immediate: true }
  );
}
